import { useState, useEffect } from "react";
import { api, formatCurrency } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Loader2, Trash2, AlertTriangle } from "lucide-react";

/**
 * Dialog qui liste les groupes de commandes doublons et permet d'en
 * sélectionner pour suppression.
 */
export const OrderDuplicatesDialog = ({ open, onClose, onChanged }) => {
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  const [selected, setSelected] = useState({}); // {order_id: true}
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await api.findOrderDuplicates({ tolerance_percent: 5 });
      setGroups(res.data.groups || []);
      setSelected({});
    } catch {
      toast.error("Erreur lors de la détection des doublons");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) fetchData();
  }, [open]);

  const selectedIds = Object.keys(selected).filter((k) => selected[k]);

  const toggleOne = (id) => {
    setSelected((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await Promise.all(selectedIds.map((id) => api.deleteOrder(id)));
      toast.success(`${selectedIds.length} commande(s) supprimée(s)`);
      setConfirmOpen(false);
      await fetchData();
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la suppression");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="order-duplicates-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              Détection de doublons — Commandes
            </DialogTitle>
            <DialogDescription>
              Critères : même client, même ville, montant HT à ±5 %. Cochez les commandes à supprimer.
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : groups.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Aucun doublon détecté.
            </div>
          ) : (
            <div className="space-y-4">
              {groups.map((g, gi) => (
                <Card key={gi} data-testid={`order-dup-group-${gi}`}>
                  <CardContent className="pt-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-semibold">{g.client_name}</div>
                        <div className="text-xs text-muted-foreground">
                          {g.city || "—"} · Montant {formatCurrency(g.amount_min)}
                          {g.amount_max > g.amount_min ? ` – ${formatCurrency(g.amount_max)}` : ""}
                        </div>
                      </div>
                      <Badge variant="outline" className="bg-amber-50 border-amber-300">
                        {g.count} doublons
                      </Badge>
                    </div>
                    <div className="border rounded-sm">
                      <table className="data-table text-sm">
                        <thead>
                          <tr>
                            <th className="w-10"></th>
                            <th>Référence</th>
                            <th>Date</th>
                            <th className="text-right">Montant HT</th>
                            <th>Statut</th>
                          </tr>
                        </thead>
                        <tbody>
                          {g.orders.map((o) => (
                            <tr key={o.id}>
                              <td>
                                <Checkbox
                                  checked={!!selected[o.id]}
                                  onCheckedChange={() => toggleOne(o.id)}
                                  data-testid={`dup-select-${o.id}`}
                                />
                              </td>
                              <td className="font-mono text-xs">{o.reference}</td>
                              <td className="text-xs">
                                {o.order_date
                                  ? new Date(o.order_date).toLocaleDateString("fr-FR")
                                  : "—"}
                              </td>
                              <td className="text-right currency tabular-nums">
                                {formatCurrency(o.amount_ht)}
                              </td>
                              <td>
                                <Badge variant="outline" className="text-xs">{o.status}</Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={onClose}>Fermer</Button>
            <Button
              variant="destructive"
              disabled={selectedIds.length === 0}
              onClick={() => setConfirmOpen(true)}
              data-testid="order-dup-delete-btn"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Supprimer {selectedIds.length} commande{selectedIds.length > 1 ? "s" : ""}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
            <AlertDialogDescription>
              {selectedIds.length} commande(s) seront définitivement supprimées. Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="order-dup-confirm-delete"
            >
              {deleting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Oui, supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default OrderDuplicatesDialog;
